import View                     from '@widesk-ui/components/View';
import { observer }             from 'mobx-react-lite';
import { toJS }                 from 'mobx';
import leftSidebarStore         from '@/TokamapEditor/stores/leftSidebarStore';
import { LowerOptions }         from '@/TokamapEditor/stores/leftSidebarStore';
import { Options }              from '@/TokamapEditor/stores/leftSidebarStore';
import Drawer                   from '@widesk-ui/components/Drawer';
import ButtonActions            from '@widesk-ui/components/ButtonActions';
import React                    from 'react';
import { Action }               from '@widesk-ui/components/ButtonActions';
import { ArrowLeftOutlined }    from '@ant-design/icons';
import { CloseOutlined }        from '@ant-design/icons';
import Button                   from '@widesk-ui/components/Button';
import ConfigProvider           from 'antd/lib/config-provider';
import useDesignTokens          from '@/hooks/useDesignTokens';
import useTheme                 from '@widesk-ui/hooks/useTheme';
import useIsMobile              from '@/hooks/useIsMobile';
import SheetHandle              from '@/TokamapEditor/ux/SheetHandle';
import { SHEET_DEFAULT_HEIGHT } from '@/TokamapEditor/ux/constants';

const LeftSidebar = observer(() => {
	const panels = leftSidebarStore.panels;
	const theme = useTheme();
	const [sheetHeight, setSheetHeight] = React.useState(SHEET_DEFAULT_HEIGHT);

	const field = { controlHeight: 40, colorBgContainer: theme.colorBgElevated };

	const sidebarTheme = {
		components: {
			Input: field,
			InputNumber: field,
			Select: field,
			ColorPicker: { ...field, paddingXXS: 8 },
			DatePicker: field,
			Button: { controlHeight: 40 },
			Slider: { railSize: 6, handleSize: 12, handleSizeHover: 14 },
		},
	};

	return (
		<ConfigProvider variant="outlined" theme={sidebarTheme}>
			<div id="left_sidebar_drawers">
				{leftSidebarStore.opened && !!panels.length && <PanelDrawer depth={0} sheet={{ height: sheetHeight, setHeight: setSheetHeight }} />}
			</div>
		</ConfigProvider>
	);
});

/**
 * Un panneau de la pile, qui rend le suivant dans ses propres enfants — l'imbrication des `Drawer`
 * d'antd est ce qui permet au panneau du dessous d'être poussé plutôt que recouvert.
 */
type Sheet = { height: number; setHeight: (height: number) => void };

/** Part de la largeur du panneau du dessus dont le plus profond se décale. Voir `pushDistance`. */
const PUSH_RATIO = 0.45;

/**
 * De combien ce panneau s'écarte vers la droite quand d'autres s'ouvrent par-dessus.
 *
 * Chaque tiroir ne pousse que son voisin direct, et ces décalages ne se composent pas — les
 * panneaux ne sont pas imbriqués dans le DOM (voir `getContainer`). À décalage égal par niveau, les
 * deux panneaux du dessous se retrouvaient donc AU MÊME endroit, le plus profond disparaissant
 * presque entièrement derrière son voisin.
 *
 * L'écart est donc réparti : le plus profond garde exactement le décalage d'aujourd'hui — l'encombrement
 * total ne bouge pas — et les autres se placent régulièrement entre lui et le sommet. À deux
 * panneaux, la formule redonne l'ancien comportement à l'identique.
 */
const pushDistance = (panels: Options[], depth: number) => {
	const open = panels.filter(panel => !panel.closing);
	const above = open.length - 1 - depth;

	if (above <= 0) return 0;

	// `upperPush` : certains écrans veulent couvrir complètement celui qu'ils recouvrent (voir la
	// modification groupée de points d'intérêts). Le partage ne s'y applique pas.
	const next = panels.at(depth + 1);

	if ((next as LowerOptions | undefined)?.upperPush) return next?.width || 400;

	const total = (open.at(-1)?.width || 400) * PUSH_RATIO;

	return total * above / (open.length - 1);
};

const PanelDrawer = observer(({ depth, sheet }: { depth: number; sheet: Sheet; }) => {
	const theme = useTheme();
	const isMobile = useIsMobile();

	const panels = leftSidebarStore.panels;
	const options = panels.at(depth);
	const next = panels.at(depth + 1);

	if (!options) return null;

	const isRoot = depth === 0;
	const header = options.title && <LeftSidebarHeader depth={depth} {...toJS(options)} />;

	return (
		<Drawer
			title={header}
			placement={isMobile ? 'bottom' : 'left'}
			drawerRender={isMobile ? content => (
				<div className="editor-sheet" style={{ background: theme.colorBgElevated }}>
					<SheetHandle height={sheet.height} onChange={sheet.setHeight} />
					{content}
				</div>
			) : undefined}
			closable={false}
			onClose={() => isRoot ? leftSidebarStore.close() : leftSidebarStore.pop()}
			open={!options.closing}
			mask={options.overlay && !leftSidebarStore.draggingPlace}
			// La poussée retombe dès que le panneau du dessus commence à sortir, pour que les deux
			// mouvements se fassent ensemble plutôt que l'un après l'autre.
			push={isMobile ? false : next && !next.closing ? { distance: pushDistance(panels, depth) } : { distance: 0 }}
			// Le premier s'ancre dans le flux, les suivants se superposent dans le même conteneur.
			getContainer={isRoot ? false : '#left_sidebar_drawers'}
			rootStyle={isRoot ? undefined : { position: 'absolute' }}
			width={isMobile ? undefined : options.width || 400}
			height={isMobile ? `${sheet.height * 100}%` : undefined}
			zIndex={1003}
			styles={{
				body: { padding: 0 },
				content: { userSelect: 'none' },
				wrapper: isMobile ? { maxHeight: '100%' } : { maxWidth: '100%' },
				header: { padding: 0, ...(options.bordered === false ? { borderBottom: 0 } : {}) },
				footer: { padding: `${theme.marginSM}px ${theme.marginMD}px` },
			}}
			context={{
				closeDrawer: () => isRoot ? leftSidebarStore.close() : leftSidebarStore.pop(),
				footer: options.footer,
				setFooter: reactNode => leftSidebarStore.setPanelOption(depth, 'footer', reactNode),
			}}
		>
			{isRoot ? options.render() : (
				<View
					// `??` et non `||` : un contenu qui gère lui-même son padding passe `0` (voir
					// `openThemeForm`, dont les onglets doivent aller d'un bord à l'autre), ce qu'un `||`
					// retomberait sur la valeur par défaut.
					paddingV={options.padding?.at(0) ?? 'sm'}
					paddingH={options.padding?.at(1) ?? 'md'}
				>
					{options.render()}
				</View>
			)}

			{!!next && <PanelDrawer depth={depth + 1} sheet={sheet} />}
		</Drawer>
	);
});

type LeftSidebarHeaderProps = {
	title: React.ReactNode;
	parentTitle?: string;
	subTitle?: React.ReactNode;
	renderTopLeft?: () => React.ReactNode;
	renderTopRight?: () => React.ReactNode;
	closeButton?: boolean;
	onBack?: () => void;
	actions?: Action[];
	bordered?: boolean;
	largeTitle?: boolean;
	/** Position dans la pile. Au-dessus de 0, le retour et le fil d'Ariane se déduisent tout seuls. */
	depth?: number;
}

export const LeftSidebarHeader = observer((props: LeftSidebarHeaderProps) => {
	const {
		actions,
		bordered,
		title,
		subTitle,
		parentTitle,
		renderTopLeft,
		renderTopRight,
		closeButton,
		onBack,
		largeTitle,
		depth = 0,
	} = props;

	const theme = useTheme();
	const tokens = useDesignTokens();

	// Retour automatique dès qu'il y a quelque chose sous ce panneau : revenir, c'est dépiler. Un
	// `onBack` explicite reste prioritaire, pour un écran qui a sa propre idée de « précédent ».
	const back = onBack || (depth > 0 ? () => leftSidebarStore.pop() : undefined);

	// Idem pour le fil d'Ariane : le titre du panneau du dessous, quand il est une chaîne, plutôt
	// qu'un `parentTitle` recopié à la main dans chaque appelant.
	const parent = parentTitle || (() => {
		const below = leftSidebarStore.panels.at(depth - 1)?.title;

		return depth > 0 && typeof below === 'string' ? below : undefined;
	})();

	const close = () => depth > 0 ? leftSidebarStore.pop() : leftSidebarStore.close();

	return (
		<View
			row
			paddingV="md"
			paddingH={largeTitle ? 'lg' : 'md'}
			gap="md"
			center
			style={{
				position: 'relative',
				// Sans ça, le conteneur de titre d'antd prend la largeur minimale du contenu (titre,
				// pastille de type, boutons) et la sidebar défile horizontalement.
				contain: 'inline-size',
				...(bordered ? { borderBottom: `1px solid ${theme.colorBorderSecondary}` } : {}),
			}}
		>
			<View flex bold size={largeTitle ? 20 : 16} row center gap={largeTitle ? 'sm' : 'xs'} style={{ minWidth: 0 }}>
				{/* Après le retour, pas avant : la vignette appartient au titre qu'elle illustre (voir
				    `ShapeSwatch`, `PlaceHeaderImage`), alors que la flèche appartient à la navigation. Les
				    deux panneaux qui s'en servaient déjà sont à la racine, sans flèche — rien ne bouge pour
				    eux. */}
				{!!back && (
					<IconCircle onClick={back}>
						<ArrowLeftOutlined />
					</IconCircle>
				)}

				{renderTopLeft?.()}

				{/* Les trois lignes ne sont posées que si elles ont quelque chose à dire, et l'écart passe par
				    un `gap` plutôt que par des marges portées par chacune. Auparavant la ligne de
				    sous-titre était rendue même vide, avec son `mt` : elle ajoutait 4px sous le titre, et
				    le bloc — centré dans la rangée — se retrouvait décalé vers le haut par rapport à la
				    vignette. Même chose pour le `mt` du titre quand il n'y avait pas de fil d'Ariane. */}
				<View flex gap={2} style={{ minWidth: 0 }}>
					{!!parent && (largeTitle ? (
						<View
							lineHeight={1.2}
							color={theme.colorTextDescription}
							size={11.5}
							uppercase
							style={{ letterSpacing: '.07em' }}
						>
							{parent}
						</View>
					) : (
						<View lineHeight={1.2} color={theme.colorTextLabel} size="small">{parent}</View>
					))}

					<View lineHeight={1.25}>{title}</View>

					{!!subTitle && (
						<View
							lineHeight={1.2}
							color={theme.colorTextDescription}
							size="small"
							style={{ fontWeight: '600', marginTop: largeTitle ? undefined : 4 }}
						>
							{subTitle}
						</View>
					)}
				</View>

				{renderTopRight?.()}

				{/* Pas de `forceDropdown` : `ButtonActions` affiche de lui-même l'icône de l'action quand il
				    n'y en a qu'une (libellé en infobulle, et en bleu si `Action.primary`), et retombe sur le
				    menu `…` dès qu'il y en a plusieurs. */}
				{!!actions?.length && (
					<ButtonActions
						style={{ borderRadius: 8, padding: 0, width: 30, height: 30, flexShrink: 0, color: tokens.text.secondary }}
						actions={actions}
					/>
				)}

			</View>

			{/* La croix se pose dans le coin plutôt que dans la rangée, pour laisser sa largeur au titre,
			    à ce qui l'accompagne et aux actions. */}
			{!!closeButton && (
				<View style={{ position: 'absolute', top: 4, right: 4 }}>
					<IconCircle small onClick={close}>
						<CloseOutlined />
					</IconCircle>
				</View>
			)}
		</View>
	);
});

const IconCircle = (props: {
	children: React.ReactNode;
	onClick?: () => void;
	small?: boolean;
}) => {
	const tokens = useDesignTokens();
	const size = props.small ? 24 : 28;

	return (
		<Button
			type="text"
			onClick={props.onClick}
			style={{ borderRadius: 6, padding: 0, width: size, height: size, flexShrink: 0, color: tokens.text.muted, fontSize: props.small ? 12 : undefined }}
		>
			{props.children}
		</Button>
	);
};

export default LeftSidebar;